
import React, { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLanguage } from '@/hooks/useLanguage';
import { Footer } from '@/components/Footer';
import { useToast } from "@/hooks/use-toast";
import { Helmet } from 'react-helmet';

const Contact = () => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      console.log('Form submitted with data:', formData);
      toast({
        title: language === 'de' ? 'Nachricht gesendet!' : 'Message sent!',
        description: language === 'de' 
          ? 'Vielen Dank für Ihre Nachricht. Wir werden uns in Kürze bei Ihnen melden.' 
          : 'Thank you for your message. We will get back to you shortly.',
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  const translations = {
    en: {
      contactMe: 'Contact Me',
      getInTouch: 'Get In Touch',
      yourName: 'Your Name',
      enterName: 'Enter your full name',
      emailAddress: 'Email Address',
      enterEmail: 'Enter your email',
      subject: 'Subject',
      subjectPlaceholder: 'What is this regarding?',
      message: 'Message',
      messagePlaceholder: 'Tell me more about your project or inquiry',
      sendMessage: 'Send Message',
      contactInformation: 'Contact Information',
      email: 'Email',
      phone: 'Phone',
      studioLocation: 'Studio Location'
    },
    de: {
      contactMe: 'Kontakt',
      getInTouch: 'Kontaktieren Sie mich',
      yourName: 'Ihr Name',
      enterName: 'Geben Sie Ihren vollständigen Namen ein',
      emailAddress: 'E-Mail-Adresse',
      enterEmail: 'Geben Sie Ihre E-Mail-Adresse ein',
      subject: 'Betreff',
      subjectPlaceholder: 'Worum geht es?',
      message: 'Nachricht',
      messagePlaceholder: 'Erzählen Sie mir mehr über Ihr Projekt oder Ihre Anfrage',
      sendMessage: 'Nachricht senden',
      contactInformation: 'Kontaktinformationen',
      email: 'E-Mail',
      phone: 'Telefon',
      studioLocation: 'Standort'
    }
  };

  // Select the appropriate translation based on the current language
  const currentTranslation = language === 'de' ? translations.de : translations.en;

  return (
    <div className="min-h-screen flex flex-col">
	<Helmet>
  <title>Kontakt | Matthias Meyfarth – Videograf, Fotograf & Webdesigner aus Berlin</title>
  <meta name="description" content="Kontaktieren Sie Matthias Meyfarth aus Berlin – professionelle Videografie, Fotografie und Webdesign für Ihr Unternehmen oder Projekt." />
  <meta property="og:title" content="Kontakt | Matthias Meyfarth – Videograf, Fotograf & Webdesigner aus Berlin" />
  <meta property="og:description" content="Jetzt unverbindlich anfragen: professionelle visuelle Inhalte von Matthias Meyfarth aus Berlin." />
  <meta property="og:type" content="website" />
</Helmet>
      <Navigation />
      
      <main className="relative z-10 pt-24 flex-grow">
        <section className="content-section">
          <div className="container mx-auto px-6 py-24">
            <h1 className="text-4xl font-bold mb-8">{currentTranslation.contactMe}</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {/* Contact Form */}
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-semibold mb-6">{currentTranslation.getInTouch}</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-1">
                      {currentTranslation.yourName}
                    </label>
                    <Input 
                      id="name" 
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={currentTranslation.enterName} 
                      required 
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-1">
                      {currentTranslation.emailAddress}
                    </label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={currentTranslation.enterEmail} 
                      required 
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium mb-1">
                      {currentTranslation.subject}
                    </label>
                    <Input 
                      id="subject" 
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder={currentTranslation.subjectPlaceholder} 
                      required 
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-1">
                      {currentTranslation.message}
                    </label>
                    <Textarea 
                      id="message" 
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={currentTranslation.messagePlaceholder} 
                      rows={5}
                      required 
                    />
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 
                      (language === 'de' ? 'Wird gesendet...' : 'Sending...') : 
                      currentTranslation.sendMessage}
                  </Button>
                </form>
              </div>
              
              {/* Contact Information */}
              <div className="flex flex-col justify-center">
                <div className="space-y-8">
                  <h2 className="text-2xl font-semibold mb-6">{currentTranslation.contactInformation}</h2>
                  
                  <div className="flex items-start space-x-4">
                    <Mail className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-medium">{currentTranslation.email}</h3>
                      <p className="text-muted-foreground">meyf@meyf-art.de</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <Phone className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-medium">{currentTranslation.phone}</h3>
                      <p className="text-muted-foreground">+49 xxx xxx xxx</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <MapPin className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <h3 className="font-medium">{currentTranslation.studioLocation}</h3>
                      <p className="text-muted-foreground">
                        Magdalenenstraße 12<br />
                        10365 Berlin<br />
                        Deutschland
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Contact;
